import {z} from 'zod';

export const coordinateFormatSchema = z.enum(['dd', 'ddm', 'dms', 'utm']);

export const unitSystemSchema = z.enum(['metric', 'imperial']);

const experimentSchema = z.object({
    experiment: z.string(),
    bucket: z.string().nullable(),
});

const publicViewOptionsSchema = z.enum(['EVERYONE', 'FRIENDS', 'JUST_ME']);

const createdByUserSchema = z.object({
    id: z.string(),
    link: z.string(),
    displayName: z.string(),
    image: z.string().nullable(),
});

const refineAmplitudeDeviceId = (s: string) => {
    const [group1, group2, group3, group4, group5] = [
        s.slice(0, 8),
        s.slice(8, 12),
        s.slice(12, 16),
        s.slice(16, 20),
        s.slice(20),
    ];

    return z.string().uuid().safeParse(`${group1}-${group2}-${group3}-${group4}-${group5}`).success;
};

export const userSchema = z.object({
    // API weirdly returns `false`
    activities: z.array(z.string()).nullable(),

    // is a UUID, but the API returns it without dashes
    amplitudeDeviceId: z.string().refine(refineAmplitudeDeviceId),

    canAccessNonPublicSources: z.boolean(),

    coordinateFormat: coordinateFormatSchema,

    corsProxyUrl: z.union([
        z.literal(''),
        z.string().startsWith('https://cors-proxy.gaiagps.com/?jwt='),
    ]),

    // is `0` for anonymous users
    dateJoined: z.union([z.literal(0), z.number().gt(Date.parse('2001-01-1Z'))]),

    discoverAnalyticsEnabled: z.boolean(),

    displayName: z.string(),

    // empty string if not available
    email: z.string(),

    // `experimentSchema` defined separately in case a type needs to be extracted and exported
    experiments: z.array(experimentSchema),

    // Geolocation comes from MaxMind DB; should be safe to assume lng/lat are clamped
    geolocation: z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]).nullable(),

    hasMapDownload: z.boolean(),

    hasMobileApp: z.boolean(),

    hasPassword: z.boolean(),

    hasPremiumSubscription: z.boolean(),

    hasRecordedTrack: z.boolean(),

    hasSubscription: z.boolean(),

    // id: number | null;
    id: z.number().positive().nullable(),

    // image: string | null;
    image: z.string().nullable(),

    isAuthenticated: z.boolean(),

    isEligibleForTrial: z.boolean(),

    isRanger: z.boolean(),

    isStaff: z.boolean(),

    isSU: z.boolean(),

    isTeamAdmin: z.boolean(),

    migratedToRivt: z.boolean(),

    publicStatsSetting: publicViewOptionsSchema.nullable(),

    rivtUniqueId: z.string().uuid().nullable(),

    unitSystem: unitSystemSchema,

    webAnalyticsEnabled: z.boolean(),
});

export const userFromAPISchema = userSchema.extend({
    coordinateFormat: z.union([z.literal(''), coordinateFormatSchema]),
    unitSystem: z.union([z.literal(''), unitSystemSchema]),
    discoverAnalyticsEnabled: z.boolean().nullable(),
    canAccessNonPublicSources: z.nullable(z.boolean()),
    isRanger: z.boolean().nullable(),
    isSU: z.union([z.literal(0), z.literal(1)]),
});

export type User = z.infer<typeof userSchema>;
export type UserFromApi = z.infer<typeof userFromAPISchema>;
export type CreatedByUser = z.infer<typeof createdByUserSchema>;
export type CoordinateFormat = z.infer<typeof coordinateFormatSchema>;
export type UnitSystem = z.infer<typeof unitSystemSchema>;
